import React from 'react';
import { CheckCircle, XCircle, ArrowRight, RotateCcw } from 'lucide-react';

/**
 * Readiness band configuration — colors, labels, score thresholds.
 * For full assessment (150 pts): High >= 120, Moderate >= 90, Low >= 60, Not Ready < 60
 * For quick assessment (8 yes/no): High >= 7, Moderate >= 5, Low >= 3, Not Ready < 3
 */
const BAND_CONFIG = {
  high: {
    label: 'High Readiness',
    heroBg: 'rgba(16, 185, 129, 0.08)',
    heroBorder: 'rgba(16, 185, 129, 0.25)',
    scoreColor: '#047857',
    badgeBg: '#10b981',
    description: 'You are well-prepared to pursue an acquisition. Your financial, strategic, and operational readiness positions you strongly in the marketplace.',
  },
  moderate: {
    label: 'Moderate Readiness',
    heroBg: 'rgba(245, 158, 11, 0.08)',
    heroBorder: 'rgba(245, 158, 11, 0.25)',
    scoreColor: '#b45309',
    badgeBg: '#d97706',
    description: 'You have a solid foundation but some areas could be strengthened before pursuing an acquisition. Review the section breakdown below for specific opportunities.',
  },
  low: {
    label: 'Low Readiness',
    heroBg: 'rgba(249, 115, 22, 0.08)',
    heroBorder: 'rgba(249, 115, 22, 0.25)',
    scoreColor: '#c2410c',
    badgeBg: '#ea580c',
    description: 'Several areas need attention before you are ready to pursue an acquisition. Focus on the sections with the lowest scores to build your readiness.',
  },
  not_ready: {
    label: 'Not Yet Ready',
    heroBg: 'rgba(239, 68, 68, 0.08)',
    heroBorder: 'rgba(239, 68, 68, 0.25)',
    scoreColor: '#b91c1c',
    badgeBg: '#dc2626',
    description: 'Significant preparation is needed before pursuing an acquisition. Consider working with an advisor to develop a readiness plan.',
  },
};

const SECTION_LABELS = {
  self_assessment: 'Self Assessment',
  financial_due_diligence: 'Financial Due Diligence',
  acquisition_process: 'Acquisition Process',
  business_fit: 'Business Fit',
};

function ScoreBar({ score, maxScore, label }) {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-sm font-medium text-succession-text-primary">{label}</span>
        <span className="text-sm font-mono text-succession-text-secondary">
          {score} / {maxScore}
        </span>
      </div>
      <div className="h-2 bg-succession-bg-tertiary rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${pct}%`,
            backgroundColor: pct >= 80 ? '#10b981' : pct >= 60 ? '#f59e0b' : pct >= 40 ? '#f97316' : '#ef4444',
          }}
        />
      </div>
    </div>
  );
}

function QuickAssessmentSummary({ sectionScores }) {
  if (!sectionScores) return null;

  const entries = Object.entries(sectionScores);
  return (
    <div className="space-y-3">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="flex items-center gap-3 p-3 rounded-lg bg-succession-bg-secondary border border-succession-border"
        >
          {value === 1 ? (
            <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
          ) : (
            <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          )}
          <span className="text-sm text-succession-text-primary">
            {SECTION_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function AssessmentResults({
  totalScore,
  sectionScores,
  readinessBand,
  bandLabel,
  assessmentType,
  onRetake,
}) {
  const band = BAND_CONFIG[readinessBand] || BAND_CONFIG.not_ready;
  const isFullAssessment = assessmentType === 'buyer_full';
  const maxTotal = isFullAssessment ? 150 : 8;

  // Section max scores: questions per section x 5 (Likert max)
  // S1: 8 questions = 40, S2: 8 = 40, S3: 8 = 40, S4: 6 = 30
  const sectionMaxScores = {
    self_assessment: 40,
    financial_due_diligence: 40,
    acquisition_process: 40,
    business_fit: 30,
  };

  return (
    <div className="min-h-screen bg-succession-bg-secondary flex items-center justify-center p-4 md:p-8">
      <div className="w-full max-w-2xl">
        {/* Score hero */}
        <div
          className="rounded-2xl border p-8 md:p-10 text-center mb-6"
          style={{
            backgroundColor: band.heroBg,
            borderColor: band.heroBorder,
            animation: 'fadeInUp 0.5s ease-out',
          }}
        >
          {/* Readiness badge */}
          <div className="mb-6">
            <span
              className="inline-block px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-wider text-white shadow-sm"
              style={{ backgroundColor: band.badgeBg, letterSpacing: '0.08em' }}
            >
              {bandLabel || band.label}
            </span>
          </div>

          {/* Score display */}
          <div className="mb-4">
            <span
              className="text-6xl md:text-7xl font-light"
              style={{ color: band.scoreColor }}
            >
              {totalScore}
            </span>
            <span className="text-2xl text-succession-text-secondary font-light">
              {' '}/{' '}{maxTotal}
            </span>
          </div>

          <p className="text-sm text-succession-text-secondary max-w-md mx-auto leading-relaxed">
            {band.description}
          </p>
        </div>

        {/* Section breakdown */}
        {sectionScores && Object.keys(sectionScores).length > 0 && (
          <div
            className="bg-succession-bg-primary rounded-2xl border border-succession-border p-6 md:p-8 mb-6 shadow-succession"
            style={{ animation: 'fadeInUp 0.5s ease-out 0.15s both' }}
          >
            <h3 className="text-lg font-semibold text-succession-text-primary mb-6">
              {isFullAssessment ? 'Section Breakdown' : 'Your Answers'}
            </h3>

            {isFullAssessment ? (
              <div>
                {Object.entries(sectionScores).map(([section, score]) => (
                  <ScoreBar
                    key={section}
                    score={score}
                    maxScore={sectionMaxScores[section] || 35}
                    label={SECTION_LABELS[section] || section.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  />
                ))}
              </div>
            ) : (
              <QuickAssessmentSummary sectionScores={sectionScores} />
            )}
          </div>
        )}

        {/* Action buttons */}
        <div
          className="flex flex-col sm:flex-row gap-3"
          style={{ animation: 'fadeInUp 0.5s ease-out 0.3s both' }}
        >
          <a
            href="/succession/search"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-succession-primary text-white rounded-xl px-6 py-3.5 font-medium text-sm hover:bg-succession-primary-hover transition-colors no-underline"
          >
            Browse Available Sellers
            <ArrowRight className="w-4 h-4" />
          </a>

          <button
            onClick={onRetake}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-succession-bg-primary text-succession-text-primary border border-succession-border rounded-xl px-6 py-3.5 font-medium text-sm hover:bg-succession-bg-tertiary transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Assessment
          </button>
        </div>

        {/* Fade-in animation keyframes */}
        <style>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
